import React from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, TrendingUp, Trophy, Medal } from 'lucide-react';

const CommitteeMeritReportTab = ({
  meritReport,
  meritLoading,
  selectedGpaScale,
  handleScaleChange,
  fetchMeritReport
}) => {
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-greek-blue" />
            Applicant Merit Rankings
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Candidates ranked by standardized GPA and committee scores
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-slate-600">GPA Scale:</Label>
            <Select value={String(selectedGpaScale)} onValueChange={(v) => handleScaleChange(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Scale" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="4">4.0 Scale</SelectItem>
                <SelectItem value="10">10.0 Scale</SelectItem>
                <SelectItem value="12">12.0 Scale</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button 
            onClick={() => fetchMeritReport()} 
            disabled={meritLoading}
            className="bg-greek-blue hover:bg-greek-blue-dark"
          >
            {meritLoading ? 'Loading...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {!meritReport && !meritLoading && (
        <div className="p-12 text-center">
          <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h4 className="font-medium text-slate-700 mb-2">No Report Generated</h4>
          <p className="text-sm text-slate-500 mb-4">
            Click "Generate Report" to see applicants ranked by merit
          </p>
        </div>
      )}

      {meritLoading && (
        <div className="p-12 text-center">
          <div className="w-8 h-8 border-4 border-greek-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Generating merit report...</p>
        </div>
      )}

      {meritReport && !meritLoading && (
        <div className="p-4">
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <p className="text-sm text-blue-800">
              <strong>Merit Score Calculation:</strong> GPA contributes 40%, Committee Score contributes 60%
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Report generated: {new Date(meritReport.generated_at).toLocaleString()}
            </p>
          </div>

          {meritReport.scholarships?.map((scholarship) => (
            <div key={scholarship.scholarship_id} className="mb-6 border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      {scholarship.scholarship_name}
                    </h4>
                    {scholarship.award_amount && (
                      <p className="text-sm text-slate-500">Award: ${scholarship.award_amount.toLocaleString()}</p>
                    )}
                  </div>
                  <span className="text-sm text-slate-600">
                    {scholarship.total_applicants} applicant(s)
                  </span>
                </div>
              </div>

              {scholarship.rankings?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-slate-700">Rank</th>
                        <th className="px-4 py-2 text-left font-medium text-slate-700">Applicant</th>
                        <th className="px-4 py-2 text-center font-medium text-slate-700">
                          GPA ({selectedGpaScale}.0)
                        </th>
                        <th className="px-4 py-2 text-center font-medium text-slate-700">Committee Score</th>
                        <th className="px-4 py-2 text-center font-medium text-slate-700">Merit Score</th>
                        <th className="px-4 py-2 text-center font-medium text-slate-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scholarship.rankings.map((applicant, idx) => (
                        <tr 
                          key={applicant.application_id} 
                          className={`border-t border-slate-100 ${idx < 3 ? 'bg-amber-50' : ''}`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {applicant.rank <= 3 ? (
                                <Medal className={`w-5 h-5 ${
                                  applicant.rank === 1 ? 'text-yellow-500' : 
                                  applicant.rank === 2 ? 'text-slate-400' : 
                                  'text-amber-600'
                                }`} />
                              ) : (
                                <span className="w-5 h-5 flex items-center justify-center text-slate-500 font-medium">
                                  {applicant.rank}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium text-slate-900">{applicant.applicant_name}</p>
                              {applicant.gpa_data?.institution && (
                                <p className="text-xs text-slate-500">{applicant.gpa_data.institution}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {applicant.gpa_data ? (
                              <div>
                                <span className="font-semibold text-blue-700">
                                  {applicant.gpa_data.normalized_gpa}
                                </span>
                                <p className="text-xs text-slate-400">
                                  (was {applicant.gpa_data.original_gpa}/{applicant.gpa_data.original_scale})
                                </p>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">No transcript</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {applicant.committee_scoring ? (
                              <div>
                                <span className="font-medium">
                                  {applicant.committee_scoring.average_score}/{applicant.committee_scoring.max_possible}
                                </span>
                                <p className="text-xs text-slate-500">
                                  ({applicant.committee_scoring.num_reviewers} reviewer{applicant.committee_scoring.num_reviewers !== 1 ? 's' : ''})
                                </p>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-xs">Not scored</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {applicant.merit_score !== null ? (
                              <span className={`font-bold text-lg ${
                                applicant.merit_score >= 80 ? 'text-green-600' :
                                applicant.merit_score >= 60 ? 'text-blue-600' :
                                'text-slate-600'
                              }`}>
                                {applicant.merit_score}%
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              applicant.status === 'awarded' ? 'bg-green-100 text-green-700' :
                              applicant.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {applicant.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-6 text-center text-slate-500">
                  No submitted applications for this scholarship
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CommitteeMeritReportTab;